import React from 'react'
import { createPortal } from 'react-dom'
import { VideoCameraIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text } from '../ui'
import { PsychologicalAttendForm } from './PsychologicalAttendForm'
import { SimpleAttendanceNotesForm } from './SimpleAttendanceNotesForm'
import { AppointmentAttachmentsList } from './AppointmentAttachmentsList'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import {
  ATTENDANCE_TIME_WINDOW_DISABLED,
  attendanceEarlyMessage,
  attendancePastMessage,
  canPerformAttendance,
  isAppointmentBeforeScheduled,
  isAppointmentPastForAttendance,
} from '../../services/supportCenterAttendance'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import {
  ASSISTANCE_FORM_KIND_MISSING,
  SUPPORT_CENTER_FORM_KIND_LABELS,
  appointmentFormKind,
} from '../../types/supportCenterSpecialties'
import { attendanceNotesLabel, isReducedTriageForm } from '../../types/supportCenterTriage'
import { DEFAULT_SCHEDULING_LIMITS } from '../../types/supportCenterParameters'
import { formatDateOnlyPtBr } from '../../utils/dateOnly'
import { formatCpf, patientKinshipLabel } from './dashboardHelpers'

type AppointmentAttendModalProps = {
  open: boolean
  appointment: SupportCenterAgendaEvent | null
  canUpdate: boolean
  pastToleranceMinutes?: number
  onClose: () => void
  onCompleted: () => void
}

function meetingLinkOf(appointment: SupportCenterAgendaEvent): string {
  return (appointment.call_link || appointment.call_link_short || '').trim()
}

function GeneralField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" color="muted">
        {label}
      </Text>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{value || '—'}</div>
    </div>
  )
}

/**
 * Atendimento pela agenda: prontuário psicológico ou texto único
 * (observações / orientação) conforme o form_kind.
 */
export const AppointmentAttendModal: React.FC<AppointmentAttendModalProps> = ({
  open,
  appointment,
  canUpdate,
  pastToleranceMinutes = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  onClose,
  onCompleted,
}) => {
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [confirmCompleteOpen, setConfirmCompleteOpen] = React.useState(false)
  const [actionsHost, setActionsHost] = React.useState<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!open) return
    setError(null)
    setSaving(false)
    setConfirmCompleteOpen(false)
  }, [open, appointment?.id])

  if (!open || !appointment) return null

  const formKind = appointmentFormKind(appointment)
  const isPsychological = formKind === 'psicologico'
  const isNotesForm = isReducedTriageForm(formKind)
  const notesLabel = attendanceNotesLabel(formKind)
  const formLabel = formKind ? SUPPORT_CENTER_FORM_KIND_LABELS[formKind] : null
  const readOnly = appointment.status === 'realizado' || !!appointment.attendance_completed_at
  const triageOk = appointment.triage_status === 'concluida'
  const canMarkDone =
    !readOnly && canPerformAttendance(appointment, canUpdate, pastToleranceMinutes)
  const isFallbackForm = Boolean(formKind) && !isPsychological && !isNotesForm
  const meetingLink = meetingLinkOf(appointment)

  const openMeetingLink = () => {
    if (!meetingLink) return
    window.open(meetingLink, '_blank', 'noopener,noreferrer')
  }

  const handleMarkRealizado = async () => {
    if (!canMarkDone) return
    if (!triageOk) {
      setError('Triagem pendente ou ausente; atendimento não permitido.')
      setConfirmCompleteOpen(false)
      return
    }
    if (!ATTENDANCE_TIME_WINDOW_DISABLED) {
      if (isAppointmentBeforeScheduled(appointment.scheduled_at)) {
        setError(attendanceEarlyMessage())
        setConfirmCompleteOpen(false)
        return
      }
      if (isAppointmentPastForAttendance(appointment.scheduled_at, pastToleranceMinutes)) {
        setError(attendancePastMessage(pastToleranceMinutes))
        setConfirmCompleteOpen(false)
        return
      }
    }
    setSaving(true)
    setError(null)
    try {
      await supportCenterAppointmentsService.markRealizado(appointment.id)
      setConfirmCompleteOpen(false)
      onCompleted()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao marcar atendimento.')
    } finally {
      setSaving(false)
    }
  }

  const handlePsychSubmitted = () => {
    onCompleted()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-attend-modal-title"
        className={`bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-h-[92vh] flex flex-col ${
          isPsychological || isNotesForm ? 'max-w-3xl' : 'max-w-lg'
        }`}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="appointment-attend-modal-title"
              className="text-gray-900 dark:text-white"
            >
              {readOnly ? 'Atendimento realizado' : 'Realizar atendimento'}
            </Heading>
          </div>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Fechar
          </Button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-6 flex-1 min-h-0">
        <div className="space-y-3">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
            Dados gerais
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
            <GeneralField
              label="Segurado"
              value={appointment.holder_people?.name}
            />
            <GeneralField
              label="Data de nascimento"
              value={formatDateOnlyPtBr(appointment.holder_people?.captured_birth_date)}
            />
            <GeneralField label="CPF" value={formatCpf(appointment.holder_cpf)} />
            <GeneralField label="Paciente" value={appointment.patient_people?.name} />
            <GeneralField label="CPF do paciente" value={formatCpf(appointment.patient_cpf)} />
            <GeneralField
              label="Grau de parentesco"
              value={patientKinshipLabel(appointment)}
            />
            <GeneralField label="Assistência" value={appointment.assistance?.name} />
          </div>
        </div>

        <AppointmentAttachmentsList
          appointmentId={appointment.id}
          variant="section"
          title="Anexos"
          canUpload={canUpdate}
        />

        <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Formulário específico
              </Heading>
              {formLabel ? (
                <Text size="sm" color="muted">
                  {formLabel}
                </Text>
              ) : null}
            </div>
          </div>

          {!formKind ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              {ASSISTANCE_FORM_KIND_MISSING}
            </div>
          ) : readOnly && isPsychological ? (
            <PsychologicalAttendanceView appointmentId={appointment.id} />
          ) : isPsychological ? (
            triageOk ? (
              <PsychologicalAttendForm
                appointment={appointment}
                canSubmit={canMarkDone}
                onClose={onClose}
                onSubmitted={handlePsychSubmitted}
                actionsHost={actionsHost}
              />
            ) : (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                Triagem pendente ou ausente; atendimento não permitido. Conclua a triagem antes de
                realizar o atendimento.
              </div>
            )
          ) : isNotesForm ? (
            triageOk || readOnly ? (
              <SimpleAttendanceNotesForm
                appointment={appointment}
                formKind={formKind}
                canSubmit={canMarkDone}
                label={notesLabel}
                onClose={onClose}
                onSubmitted={handlePsychSubmitted}
                actionsHost={actionsHost}
              />
            ) : (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                Triagem pendente ou ausente; atendimento não permitido. Conclua a triagem antes de
                realizar o atendimento.
              </div>
            )
          ) : (
            <div className="space-y-4">
              {!triageOk && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                  Triagem pendente ou ausente; atendimento não permitido.
                </div>
              )}
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                Em breve: formulário específico por profissional
                {formLabel ? ` (${formLabel})` : ''}. Por enquanto você pode apenas marcar o
                agendamento como realizado.
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-900">
          <Button
            variant="outline"
            onClick={openMeetingLink}
            disabled={!meetingLink || saving}
            title={
              meetingLink
                ? 'Abrir o link da reunião em uma nova aba'
                : 'Link da reunião ainda não disponível.'
            }
          >
            <VideoCameraIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            Acessar reunião
          </Button>
          <div ref={setActionsHost} className="flex flex-wrap justify-end gap-3">
            {isFallbackForm && (
              <>
                <Button variant="ghost" onClick={onClose} disabled={saving}>
                  Cancelar
                </Button>
                {canMarkDone && (
                  <Button
                    onClick={() => {
                      setError(null)
                      setConfirmCompleteOpen(true)
                    }}
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : 'Concluir atendimento'}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {confirmCompleteOpen &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Concluir atendimento
              </Heading>
              <Text size="sm" color="muted">
                Ao confirmar, o atendimento será marcado como realizado. Deseja continuar?
              </Text>
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setConfirmCompleteOpen(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button onClick={() => void handleMarkRealizado()} disabled={saving}>
                  {saving ? 'Salvando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

const PSYCH_VIEW_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'demand_notes', label: 'Demanda trazida' },
  { key: 'intervention_notes', label: 'Intervenção realizada' },
  { key: 'patient_response', label: 'Resposta do paciente' },
  { key: 'emotional_state', label: 'Estado emocional' },
  { key: 'risk_indicator', label: 'Indicador de risco' },
  { key: 'referral', label: 'Encaminhamento' },
  { key: 'additional_notes', label: 'Observações adicionais' },
]

function PsychologicalAttendanceView({ appointmentId }: { appointmentId: string }) {
  const [row, setRow] = React.useState<Record<string, string | null> | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    void supportCenterAttendService
      .getPsychologicalAttendance(appointmentId)
      .then((data) => {
        if (!cancelled) setRow(data)
      })
      .catch((e) => {
        if (!cancelled) {
          setRow(null)
          setError(e instanceof Error ? e.message : 'Não foi possível carregar o prontuário.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appointmentId])

  if (loading) {
    return (
      <Text size="sm" color="muted">
        Carregando prontuário...
      </Text>
    )
  }
  if (error) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
        {error}
      </div>
    )
  }
  if (!row) {
    return (
      <Text size="sm" color="muted">
        Nenhum prontuário gravado para este atendimento.
      </Text>
    )
  }

  return (
    <div className="space-y-3">
      {PSYCH_VIEW_FIELDS.map((field) => (
        <div key={field.key}>
          <Text size="xs" color="muted">
            {field.label}
          </Text>
          <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
            {row[field.key]?.trim() || '—'}
          </div>
        </div>
      ))}
    </div>
  )
}
